/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { fetchGraphQl } from '../fetch-graphql';
import {
  GET_ATTRIBUTES_FORM,
  GET_ATTRIBUTES_FORM_SHORT,
} from './graphql/getAttributesForm.graphql';
import { handleNetworkError } from '@/account/lib/network-error';
import { handleFetchError } from '@/account/lib/fetch-error';
import { transformAttributesForm } from '@/account/data/transforms';
import { AttributesFormModel } from '@/account/data/models';
import { GetAttributesFormResponse } from '@/account/types';

export const getAttributesForm = async (
  formCode: string
): Promise<AttributesFormModel[]> => {
  const sessionStorageKey = `_account_attributesForm_${formCode}`;

  const sessionStorageCache = sessionStorage.getItem(sessionStorageKey);

  if (sessionStorageCache) {
    return JSON.parse(sessionStorageCache);
  }

  return await fetchGraphQl(
    formCode !== 'shortRequest'
      ? GET_ATTRIBUTES_FORM
      : GET_ATTRIBUTES_FORM_SHORT,
    {
      method: 'GET',
      cache: 'force-cache',
      variables: { formCode },
    }
  )
    .then((response: GetAttributesFormResponse) => {
      if (response.errors?.length) return handleFetchError(response.errors);

      const transformedData = transformAttributesForm(response);

      sessionStorage.setItem(
        sessionStorageKey,
        JSON.stringify(transformedData)
      );

      return transformedData;
    })
    .catch(handleNetworkError);
};
